import { useEffect, useState } from "react";
import { adminApi } from "../api/adminApi";
import { useAuth } from "../auth/AuthContext";

export interface OnboardingStep {
  id: string;
  title: string;
  why: string;
  /** Where the "Do this" button goes. */
  to: string;
  cta: string;
  done: boolean;
  /** Optional steps don't hold the checklist open. */
  optional?: boolean;
}

export interface OnboardingState {
  loading: boolean;
  steps: OnboardingStep[];
  /** Key of the first report the tenant owns, used to make the API examples concrete. */
  firstReportKey: string | null;
  doneCount: number;
  requiredDone: boolean;
}

/** Works out how far a tenant has got from what actually exists in their workspace (reports, keys,
 *  schedules...) instead of asking them to tick boxes. Any lookup that fails (e.g. a viewer without
 *  permission to list users) simply doesn't count for or against them. */
export function useOnboarding(): OnboardingState {
  const { hasRole } = useAuth();
  const [state, setState] = useState<{ loading: boolean; data: Record<string, unknown> }>({ loading: true, data: {} });
  const canManage = hasRole("OWNER", "ADMIN");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [branding, reports, keys, users, schedules] = await Promise.allSettled([
        adminApi.branding.get(),
        adminApi.reports.list(),
        canManage ? adminApi.apiKeys.list() : Promise.resolve(null),
        canManage ? adminApi.users.list() : Promise.resolve(null),
        adminApi.schedules.list(),
      ]);
      const own = reports.status === "fulfilled" ? reports.value.filter((r) => !r.shared) : [];
      let hasEndpoint = false;
      if (own[0]) {
        hasEndpoint = await adminApi.reports.getEndpoint(own[0].id).then((e) => !!e).catch(() => false);
      }
      if (cancelled) return;
      const b = branding.status === "fulfilled" ? branding.value : null;
      setState({
        loading: false,
        data: {
          branded: !!(b && (b.companyName || b.logoDataUri)),
          reports: own,
          hasEndpoint,
          keys: keys.status === "fulfilled" && keys.value ? keys.value.filter((k) => k.status === "ACTIVE").length : null,
          users: users.status === "fulfilled" && users.value ? users.value.length : null,
          schedules: schedules.status === "fulfilled" ? schedules.value.length : 0,
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const d = state.data as {
    branded?: boolean;
    reports?: { id: string; reportKey: string; status: string }[];
    hasEndpoint?: boolean;
    keys?: number | null;
    users?: number | null;
    schedules?: number;
  };
  const reports = d.reports ?? [];
  const first = reports[0];

  const steps: OnboardingStep[] = [
    {
      id: "branding",
      title: "Add your branding",
      why: "Your logo, colours and company details fill in automatically on every report.",
      to: "/settings/branding",
      cta: "Set branding",
      done: !!d.branded,
      optional: true,
    },
    {
      id: "report",
      title: "Create your first report",
      why: "Start from a ready-made layout — a polished statement takes a couple of clicks.",
      to: "/reports",
      cta: "Create a report",
      done: reports.length > 0,
    },
    {
      id: "data",
      title: "Connect your data",
      why: "Point the report at your system's API, then map its fields to the template. Or use sample data while you design.",
      to: first ? `/reports/${first.id}/endpoint` : "/reports",
      cta: "Connect data",
      done: !!d.hasEndpoint,
      optional: true,
    },
    {
      id: "publish",
      title: "Design and publish it",
      why: "Edit the layout with a live preview, then publish so it can be run and integrated.",
      to: first ? `/reports/${first.id}/designer` : "/reports",
      cta: "Open the designer",
      done: reports.some((r) => r.status === "PUBLISHED"),
    },
  ];
  if (d.keys !== null && d.keys !== undefined) {
    steps.push({
      id: "apikey",
      title: "Connect your application",
      why: "Create an API key so your own system can request this report as PDF, Excel, Word or CSV.",
      to: "/api-keys",
      cta: "Create an API key",
      done: d.keys > 0,
    });
  }
  if (d.users !== null && d.users !== undefined) {
    steps.push({
      id: "team",
      title: "Invite a teammate",
      why: "Give colleagues their own login — designers build reports, viewers only run them.",
      to: "/users",
      cta: "Invite someone",
      done: d.users > 1,
      optional: true,
    });
  }
  steps.push({
    id: "schedule",
    title: "Schedule delivery",
    why: "Have a report generated and emailed automatically — every Monday, month-end, whenever you choose.",
    to: "/schedules",
    cta: "Create a schedule",
    done: (d.schedules ?? 0) > 0,
    optional: true,
  });

  return {
    loading: state.loading,
    steps,
    firstReportKey: first?.reportKey ?? null,
    doneCount: steps.filter((s) => s.done).length,
    requiredDone: steps.filter((s) => !s.optional).every((s) => s.done),
  };
}
